import { KeyValue } from "@angular/common";
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Message } from '@app/core/models/message.model';
import { Publisher } from '@app/core/models/product.model';
import { AppLocaleService } from '@app/core/services/app-locale.service';
import { MessageService } from '@app/core/services/message.service';
import { UserProfilesService } from '@app/core/services/user-profiles.service';
import { StaticDataCacheService } from '@app/order/services/static-data-cache.service';
import { BehaviorSubject, combineLatest, map, of } from 'rxjs';
import { SubSink } from 'subsink';
import {AppConfigService} from '@core/services/app-config.service';
import {AppConfig} from '@core/models/app-config';

@Component({
	selector: 'app-order-conversations',
	templateUrl: './order-conversations.component.html',
	styleUrls: ['./order-conversations.component.css'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderConversationsComponent implements OnInit, OnDestroy {

	@Input()
	get orderId(): string | undefined {
		return this._orderId;
	}
	set orderId(rhs: string | undefined) {
		this._orderId = rhs;
		this.buildConversations();
	}
	private _orderId?: string;

	@Input()
	set showMessageId(id: string | undefined) {
		this.scrollToMessageId = id;
	}

	scrollToMessageId?: string;

	constructor (
		public messageService: MessageService,
		public appLocaleService: AppLocaleService,
		private _userProfileService: UserProfilesService,
		private _staticDataCacheService: StaticDataCacheService,
		private _appConfigService: AppConfigService
	) { }

	// Maps publisher to an array of messages
	conversations$ = new BehaviorSubject(new Map<Publisher | undefined, Message[]>());
	loading$ = new BehaviorSubject<boolean>(false);

	currentUserId?: string;

	publishers: Publisher[] = [];
	enableChatToPublishers: string[] = []

	expandedPanelKey?: string;

	private subs = new SubSink();

	ngOnInit() {
		this.subs.add(
			this._appConfigService.config$.subscribe((appConfig: AppConfig | undefined) => {
				if (appConfig) {
					this.enableChatToPublishers = appConfig.enableChatToPublishers ?? []
				}
			})
		);
		combineLatest([this._userProfileService.userProfile$, this._staticDataCacheService.publishers$])
			.subscribe(([userProfile, publishers]) => {
				this.currentUserId = userProfile?.id;
				this.publishers = publishers;
				this.buildConversations();
			});
	}

	ngOnDestroy() {
		this.subs.unsubscribe();
	}

	messageSent() {
		this.buildConversations();
	}

	private buildConversations() {
		if (this.publishers && this.currentUserId) {
			const conversationsSource$ = this._orderId ? this.messageService.getConversationsForOrderId(this._orderId) : of(new Map<string, Message[]>());
			this.subs.add(
				conversationsSource$.pipe(
					map(conversationMap => {
						const newMap = new Map<Publisher | undefined, Message[]>();

						for (let mapEntry of conversationMap.entries()) {
							let publisher = this.publishers.find(x => x.id === mapEntry[0]);

							if (!publisher) {
								publisher = new Publisher();
								// note: all messages with empty publisher.id will be grouped together
								publisher.id = publisher.label = mapEntry[0];
							}

							newMap.set(publisher, mapEntry[1]);

							if (this.scrollToMessageId) {
								const messages: Message[] = mapEntry[1];
								if (messages.some((msg: Message) => msg.id == this.scrollToMessageId)) {
									this.expandedPanelKey = publisher.id;
								}
							}
						}

						for (let publisher of this.publishers) {
							if (!newMap.has(publisher)) {
								newMap.set(publisher, []);
							}
						}
						return newMap;
					})
				).subscribe({
					next: (conversations) => {
						this.conversations$.next(conversations);
						this.loading$.next(true);
					},
					error: (error) => {
						this.loading$.next(true);
					}
				}));
		}
	}

	trackByFn_conversations(index: number, item: KeyValue<Publisher | undefined, Message[]>) {
		return item.key;
	}

	isChatEnabledForPublisher(id?: string): boolean {
		if (!id) {
			return false;
		}
		return this.enableChatToPublishers.includes(id);
	}
}
